import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';

interface Options<T> {
  name: string;
  init?: T;
  destroyOnUnmount?: boolean;
  keepState?: boolean;
}
export const useValueQuery = <T extends string | number | boolean | null>(options: Options<T>) => {
  const { name, init, destroyOnUnmount, keepState = true } = options;

  const navigate = useNavigate();
  const { search, state } = useLocation();
  const makeSearch = useCallback(
    (value: T) => {
      const searchParams = new URLSearchParams(window.location.search);
      if (value === null || value === undefined) {
        searchParams.delete(name);
      } else if (searchParams.has(name)) {
        searchParams.set(name, String(value));
      } else {
        searchParams.append(name, String(value));
      }
      return searchParams;
    },
    //eslint-disable-next-line
    [name],
  );

  const onChange = useCallback(
    (value: T) => {
      return navigate({ search: makeSearch(value).toString() }, { state: keepState ? state : undefined });
    },
    //eslint-disable-next-line
    [makeSearch, navigate, keepState, state],
  );
  const onClear = useCallback(() => {
    onChange(undefined as any);
  }, [onChange]);

  const value = useMemo(() => {
    const searchParams = new URLSearchParams(search);
    return searchParams.get(name) as T;
    //eslint-disable-next-line
  }, [name, search]);

  const refOnce = useRef(false);
  useEffect(() => {
    if (init && !value && !refOnce.current) {
      refOnce.current = true;
      navigate({ search: makeSearch(init).toString() }, { replace: true, state: keepState ? state : undefined });
    }
    //eslint-disable-next-line
  }, [value, init, makeSearch, keepState, state]);

  useEffect(() => {
    return () => {
      if (destroyOnUnmount) {
        const searchParams = new URLSearchParams(window.location.search);
        // Nothing to strip: leave history alone (a replace here would also carry the
        // mount-time `state` onto the next page's entry).
        if (!searchParams.has(name)) {
          return;
        }
        searchParams.delete(name);
        // The pathname is read live, like the search above. This cleanup runs when the
        // page is being LEFT, and `navigate` (captured at mount) resolves a `To` without
        // a pathname against the pathname of the render it came from -- the page being
        // left -- so `{ search }` alone rewrote the URL back to that page (G1: leaving the
        // dashboard for the patients list landed on the dashboard; task manager and
        // clinical meetings ping-ponged). history v4 resolved against the live location.
        navigate(
          { pathname: window.location.pathname, search: searchParams.toString() },
          { replace: true, state: keepState ? state : undefined },
        );
      }
    };
    //eslint-disable-next-line
  }, []);

  return { value, onChange, onClear };
};
